<template>
    <div class="assign-document-modal-overlay" @click.self="close">
        <div class="assign-document-modal">
            <div class="modal-header">
                <h4 class="modal-title">Assign Document to {{ studentName }}</h4>
                <button type="button" class="close" @click="close" :disabled="submitting">
                    <span>&times;</span>
                </button>
            </div>

            <div class="modal-body">
                <!-- Loading Templates -->
                <div v-if="loadingTemplates" class="text-center" style="padding: 40px 20px;">
                    <i class="fa fa-spinner fa-spin fa-2x"></i>
                    <p style="margin-top: 15px;">Loading templates...</p>
                </div>

                <!-- Template Selection -->
                <div v-else>
                    <div class="form-group">
                        <label for="template-select">Select Document Template</label>
                        <select
                            id="template-select"
                            class="form-control"
                            v-model="selectedTemplateId"
                            @change="onTemplateChange"
                            :disabled="submitting"
                        >
                            <option :value="null">-- Select a template --</option>
                            <option
                                v-for="template in templates"
                                :key="template.id"
                                :value="template.id"
                            >
                                {{ template.name }}
                                <span v-if="template.category"> ({{ template.category }})</span>
                            </option>
                        </select>
                    </div>

                    <!-- No Templates Available -->
                    <div v-if="templates.length === 0" class="alert alert-warning">
                        <i class="fa fa-exclamation-triangle"></i>
                        No active document templates available.
                        <a href="#/admin/document-templates">Create a template</a> first.
                    </div>

                    <!-- Selected Template Preview -->
                    <div v-if="selectedTemplate" class="template-preview">
                        <h5>Template Details</h5>
                        <div class="well well-sm">
                            <p v-if="selectedTemplate.description">
                                {{ selectedTemplate.description }}
                            </p>
                            <p v-else class="text-muted">
                                No description available.
                            </p>
                        </div>

                        <!-- Role Assignment Preview -->
                        <h5>Field Assignments</h5>
                        <div class="well well-sm">
                            <p class="text-muted" style="margin-bottom: 10px; font-size: 13px;">
                                The following roles will be assigned to complete this document:
                            </p>
                            <ul class="list-unstyled assignment-preview">
                                <li v-if="fieldsByRole.student > 0">
                                    <span class="badge badge-info">Student</span>
                                    <span>{{ student.fullname || student.firstname + ' ' + student.lastname }}</span>
                                    <small class="text-muted">({{ fieldsByRole.student }} field{{ fieldsByRole.student > 1 ? 's' : '' }})</small>
                                </li>
                                <li v-if="fieldsByRole.guardian > 0">
                                    <span class="badge badge-info">Guardian</span>
                                    <span v-if="hasGuardian">{{ guardianName }}</span>
                                    <span v-else>{{ studentName }} (Parent/Guardian)</span>
                                    <small class="text-muted">({{ fieldsByRole.guardian }} field{{ fieldsByRole.guardian > 1 ? 's' : '' }})</small>
                                </li>
                                <li v-if="fieldsByRole.staff > 0">
                                    <span class="badge badge-default">Staff</span>
                                    <span class="text-muted">To be filled by admin</span>
                                    <small class="text-muted">({{ fieldsByRole.staff }} field{{ fieldsByRole.staff > 1 ? 's' : '' }})</small>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            <div class="modal-footer">
                <button
                    type="button"
                    class="btn btn-default"
                    @click="close"
                    :disabled="submitting"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    class="btn btn-dc"
                    @click="assignDocument"
                    :disabled="!canSubmit || submitting"
                >
                    <i v-if="submitting" class="fa fa-spinner fa-spin"></i>
                    <i v-else class="fa fa-plus"></i>
                    {{ submitting ? 'Assigning...' : 'Assign Document' }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import {
    GetDocumentTemplates,
    GetDocumentTemplate,
    GenerateDocument
} from '../../../../../../../DriveScout/API/models/DocumentModel';
import { transformAnnotationsFromBackend } from '../../../../../../common/helpers/annotationTransformer';

export default {
    name: 'AssignDocumentModal',
    props: {
        student: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            loadingTemplates: true,
            submitting: false,
            templates: [],
            selectedTemplateId: null,
            selectedTemplate: null
        };
    },
    computed: {
        studentName() {
            if (!this.student) return '';
            return this.student.fullname || `${this.student.firstname} ${this.student.lastname}`;
        },
        hasGuardian() {
            return this.student && this.student.guardian && this.student.guardian.id;
        },
        guardianName() {
            if (!this.hasGuardian) return '';
            const guardian = this.student.guardian;
            return guardian.fullname || `${guardian.firstname} ${guardian.lastname}`;
        },
        fieldsByRole() {
            if (!this.selectedTemplate || !this.selectedTemplate.annotations) {
                return { student: 0, guardian: 0, staff: 0 };
            }

            // Transform backend annotations to frontend format
            const frontendAnnotations = transformAnnotationsFromBackend(this.selectedTemplate.annotations);

            const counts = { student: 0, guardian: 0, staff: 0 };
            frontendAnnotations.forEach(annotation => {
                const signeeType = annotation.signee_type || (annotation.assignableTo && annotation.assignableTo[0]) || 'student';
                if (counts.hasOwnProperty(signeeType)) {
                    counts[signeeType]++;
                }
            });
            return counts;
        },
        canSubmit() {
            return this.selectedTemplateId !== null && !this.loadingTemplates;
        }
    },
    mounted() {
        this.loadTemplates();
    },
    methods: {
        async loadTemplates() {
            this.loadingTemplates = true;
            try {
                const response = await GetDocumentTemplates({ status: 'active', per_page: 100 });
                this.templates = response.data.data || response.data || [];
            } catch (error) {
                console.error('Failed to load templates:', error);
                this.templates = [];
            } finally {
                this.loadingTemplates = false;
            }
        },

        async onTemplateChange() {
            if (!this.selectedTemplateId) {
                this.selectedTemplate = null;
                return;
            }

            // Find template in already loaded list
            this.selectedTemplate = this.templates.find(t => t.id === this.selectedTemplateId);

            // If annotations not in list data, fetch full template
            if (this.selectedTemplate && !this.selectedTemplate.annotations) {
                try {
                    const response = await GetDocumentTemplate(this.selectedTemplateId);
                    this.selectedTemplate = response.data.data?.template || response.data.template || response.data;
                } catch (error) {
                    console.error('Failed to load template details:', error);
                }
            }
        },

        async assignDocument() {
            if (!this.canSubmit || this.submitting) return;

            this.submitting = true;

            try {
                // Build assigned_users array based on template field signee types
                const assignedUsers = [];
                const fieldsBySignee = {};

                // Transform backend annotations to frontend format
                const frontendAnnotations = this.selectedTemplate.annotations
                    ? transformAnnotationsFromBackend(this.selectedTemplate.annotations)
                    : [];

                frontendAnnotations.forEach(annotation => {
                    const signeeType = annotation.signee_type || (annotation.assignableTo && annotation.assignableTo[0]) || 'student';
                    if (!fieldsBySignee[signeeType]) {
                        fieldsBySignee[signeeType] = [];
                    }
                    fieldsBySignee[signeeType].push(annotation.fieldName || annotation.id);
                });

                // Map signee types to users
                if (fieldsBySignee.student && fieldsBySignee.student.length > 0) {
                    assignedUsers.push({
                        user_id: this.student.id,
                        role: 'student',
                        fields: fieldsBySignee.student
                    });
                }

                if (fieldsBySignee.guardian && fieldsBySignee.guardian.length > 0 && this.hasGuardian) {
                    assignedUsers.push({
                        user_id: this.student.guardian.id,
                        role: 'guardian',
                        fields: fieldsBySignee.guardian
                    });
                }

                // Note: Staff fields are left unassigned for admin to fill

                const response = await GenerateDocument({
                    template_id: this.selectedTemplateId,
                    assigned_users: assignedUsers,
                    source: 'admin_assigned'
                });

                this.$emit('assigned', response.data);
            } catch (error) {
                console.error('Failed to assign document:', error);
                const message = error.response?.data?.message || 'Failed to assign document';
                alert(message);
            } finally {
                this.submitting = false;
            }
        },

        close() {
            if (this.submitting) return;
            this.$emit('close');
        }
    }
};
</script>

<style scoped>
.assign-document-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1050;
}

.assign-document-modal {
    background: #fff;
    border-radius: 6px;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
    width: 100%;
    max-width: 600px;
    max-height: 90vh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

.modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 15px 20px;
    border-bottom: 1px solid #e5e5e5;
    background-color: #f5f5f5;
}

.modal-title {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
}

.modal-header .close {
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    color: #666;
    padding: 0;
    line-height: 1;
}

.modal-header .close:hover {
    color: #333;
}

.modal-body {
    padding: 20px;
    overflow-y: auto;
    flex: 1;
}

.modal-footer {
    padding: 15px 20px;
    border-top: 1px solid #e5e5e5;
    background-color: #f5f5f5;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
}

.template-preview {
    margin-top: 20px;
}

.template-preview h5 {
    margin-bottom: 10px;
    font-weight: 600;
    color: #333;
}

.assignment-preview {
    margin: 0;
}

.assignment-preview li {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 0;
    border-bottom: 1px solid #eee;
}

.assignment-preview li:last-child {
    border-bottom: none;
}

.assignment-preview .badge {
    min-width: 70px;
    text-align: center;
}

.badge-info {
    background-color: #5bc0de;
}

.badge-default {
    background-color: #777;
}

.form-group label {
    font-weight: 600;
    margin-bottom: 8px;
    display: block;
}

.well-sm {
    padding: 12px;
}
</style>
